import { useInsertionEffect } from 'react';
import Image from 'next/image';

import { Container, Text } from '@/atoms';
import { Grid } from '@/molecules';

import type { TypeImageWordpress } from '@/components/types';
import type { TypeGridProps } from '@/molecules/grid';
import type { ElementType } from 'react';

type TypeTextColumns = {
  bloque: {
    title?: string;
    headingAlign: 'text-left' | 'text-center' | 'text-right';
    headingSize:
      | 'u-headline--h1'
      | 'u-headline--h2'
      | 'u-headline--h3'
      | 'u-headline--h4';
    headingType: ElementType;
    items?: {
      image?: TypeImageWordpress;
      title?: string;
      titleAlignDesktop?: 'left' | 'center' | 'right';
      titleAlignMobile?: 'left' | 'center' | 'right';
      titleSizeDesktop?: number;
      titleSizeMobile?: number;
      headingType?: ElementType;
      description?: string;
      descriptionAlignDesktop?: 'left' | 'center' | 'right';
      descriptionAlignMobile?: 'left' | 'center' | 'right';
      descriptionSizeDesktop?: number;
      descriptionSizeMobile?: number;
    }[];
    rowItems: TypeGridProps['md'];
    desktopPaddingTop: number;
    desktopPaddingBottom: number;
    mobilePaddingTop: number;
    mobilePaddingBottom: number;
  };
  id: string;
  className?: string;
};

export const TextColumns = ({ bloque, id, className }: TypeTextColumns) => {
  useInsertionEffect(() => {
    if (!bloque.items || bloque.items.length === 0) return;

    const styleElement = document.createElement('style');

    for (let i = 0; i < bloque.items.length; i++) {
      const item = bloque.items[i];
      if (!item) continue;

      styleElement.textContent += `
        .text-columns-${i}-${id}-title {
          ${item?.titleAlignMobile ? `text-align: ${item.titleAlignMobile}` : ''};
          ${item?.titleSizeMobile ? `font-size: ${item.titleSizeMobile}px` : ''};
        }

        @media (min-width: 768px) {
          .text-columns-${i}-${id}-title {
            ${item?.titleAlignDesktop ? `text-align: ${item.titleAlignDesktop}` : ''};
            ${item?.titleSizeDesktop ? `font-size: ${item.titleSizeDesktop}px` : ''};
          }
        }
       
        .text-columns-${i}-${id}-description {
          ${item?.descriptionAlignMobile ? `text-align: ${item.descriptionAlignMobile}` : ''};
          ${item?.descriptionSizeMobile ? `font-size: ${item.descriptionSizeMobile}px` : ''};
        }

        @media (min-width: 768px) {
          .text-columns-${i}-${id}-description {
            ${item?.descriptionAlignDesktop ? `text-align: ${item.descriptionAlignDesktop}` : ''};
            ${item?.descriptionSizeDesktop ? `font-size: ${item.descriptionSizeDesktop}px` : ''};
          }
        }
      `;
    }

    styleElement.textContent = `
      ${styleElement.textContent}

      .text-columns-${id}-padding {
        padding-top: ${bloque?.mobilePaddingTop || 0}px;
        padding-bottom: ${bloque?.mobilePaddingBottom || 0}px;
      }

      @media (min-width: 768px) {
        .text-columns-${id}-padding {
          padding-top: ${bloque?.desktopPaddingTop || 0}px;
          padding-bottom: ${bloque?.desktopPaddingBottom || 0}px;
        }
      }
    `;

    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  if (!bloque.items || bloque.items.length === 0) return null;

  return (
    <Container
      id={id}
      wrapper
      padding=""
      classNameWrapper="gap-6"
      className={`text-columns-${id}-padding px-4 ${className}`}
    >
      {bloque.title && (
        <Text
          as={bloque.headingType}
          className={`u-headline ${
            bloque?.headingSize ?? 'u-headline--h2'
          } grow ${bloque.headingAlign}`}
        >
          {bloque.title}
        </Text>
      )}

      <Grid
        xs={1}
        sm={1}
        md={bloque.rowItems}
        lg={bloque.rowItems}
        outline={false}
        className="mx-auto gap-8 md:gap-5"
      >
        {bloque.items.map((item, index) => (
          <Grid.Item key={index} className="flex flex-col gap-4">
            {item.image && (
              <Image
                src={item.image.sourceUrl}
                alt={item.image.altText}
                width={item.image.mediaDetails.width}
                height={item.image.mediaDetails.height}
                className="mx-auto object-contain object-center"
                draggable={false}
              />
            )}

            {item.title && (
              <Text
                as={item?.headingType ?? 'h2'}
                className={`u-headline text-columns-${index}-${id}-title`}
              >
                {item.title}
              </Text>
            )}

            {item.description && (
              <div
                className={`u-body text-columns-${index}-${id}-description`}
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            )}
          </Grid.Item>
        ))}
      </Grid>
    </Container>
  );
};

TextColumns.displayName = 'ColumnsText';
